/**
 * File: src/lessons/ArrowUpLesson.jsx
 * Purpose: Arrow Up tracing lesson using CoachMark (aligned at start) + modular sound FX.
 * FinTorro Module: Lessons
 *
 * Key Responsibilities
 * - Render a dashed “arrow up” guide under the run-up into the last bar.
 * - Let the learner trace a freehand stroke; score it against the guide.
 * - Gamified guidance via CoachMark + audible feedback (hint ping, miss thunk, random praise TTS).
 *
 * Inputs
 * - props.candles: [{ time, open, high, low, close }]
 * - props.onContinue?: () => void
 * - props.title?: string
 *
 * Outputs
 * - Visual UI updates, analytics events via pushDL.
 *
 * Accessibility Notes
 * - Crosshair cursor for discoverability.
 * - CoachMark respects prefers-reduced-motion.
 * - Sounds are short; speech uses system TTS and only fires after user interaction.
 *
 * Security/Privacy Notes
 * - No keys or PII; no external requests.
 *
 * Testing Notes
 * - Simulate pointer events; assert message/CTA; snapshot SVG for CoachMark presence.
 * - Mock speechSynthesis/AudioContext for unit tests.
 *
 * Changelog
 * - 2025-08-29: Modular soundFX + random praise integration. Author: ChatGPT (FinTorro)
 */

// src/onboarding/activation/lessons/ArrowUpLesson.jsx
/**
 * Arrow Up tracing lesson
 * - Draw along a dashed guide; on success, notify shell to enable bottom Continue.
 * - Middle "Continue" button removed per spec.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import ChartPane from "../kit/ChartPane";
import OverlayCanvas from "../kit/OverlayCanvas";
import { usePointerStroke } from "../kit/usePointerStroke";
import { evaluateArrowUp } from "../kit/scoring";
import { burstConfettiAt, bullPopAt, confetti } from "../kit/effects";
import { pushDL } from "../kit/analytics";
import { guideUnderRunup } from "../kit/guideStrategies";
import { CoachMark, CoachMarkStyles } from "../kit/CoachMark";
import { playHintPing, playMissThunk, celebrateSuccess, ensureUserAudioOnce } from "../kit/soundFX";

export default function ArrowUpLesson({
  candles,
  onContinue,         // kept for back-compat; triggers parent enable as well
  onComplete,         // preferred notify → enables bottom orange Continue
  title = "Draw along the up arrow"
}) {
  const [result, setResult] = useState(null);     // "done" | null
  const [message, setMessage] = useState("");
  const [hintOn, setHintOn] = useState(true);
  const startTsRef = useRef(0);

  const OverlayBlock = ({ ctx }) => {
    //useEffect(() => { ensureUserAudioOnce(); }, []);
    useEffect(() => { ensureUserAudioOnce(); }, []);

    const { overlay, logicalToX, priceToY, hostEl } = ctx;

    // Build guide under the run-up
    const guide = useMemo(() =>
      guideUnderRunup(
        { overlay, logicalToX, priceToY, candles },
        { lookback: 8, yOffsetPx: 4, rightPadPx: 160 }
      ), [overlay, logicalToX, priceToY, candles]
    );

    // Arm WebAudio/TTS once; harmless if already armed\
    useEffect(() => { ensureUserAudioOnce(); }, []);

    // Ping whenever the hint is shown
    //useEffect(() => { if (hintOn) playHintPing(); }, [hintOn]);

    useEffect(() => {
         // Only ping after the first user gesture armed audio
         if (hintOn && window.__ftAudioArmed) playHintPing();
       }, [hintOn]);

    // Stroke capture
    const { pts, start, move, finish } = usePointerStroke(12);

    const onDown = (e) => {
      ensureUserAudioOnce(); // re-arm just in case
      startTsRef.current = performance.now();
      setMessage("");
      setHintOn(false);
      start(e.nativeEvent, e.currentTarget);
    };
    const onMove = (e) => move(e.nativeEvent, e.currentTarget);

    const onUp = (e) => {
      const stroke = finish();
      if (!stroke || stroke.length < 6) return;

      const res = evaluateArrowUp(stroke, guide, overlay, {
        matchThreshold: 0.75,
        minLengthRatio: 0.18,
        minUpDeltaRatio: 0.05,
      });

      const durationMs = Math.round(Math.max(0, performance.now() - (startTsRef.current || performance.now())));

      if (res.pass) {
        // ✅ Success: message + confetti + notify parent to enable bottom Continue
        // setResult("done");
        // const successMsg = "🎉 Sharp move! +20 XP +15 Tokens";
        // setMessage(successMsg);

setResult("done");
//const phrase = celebrateSuccess();            // speaks a random one & returns it
// const successMsg = `🎉 ${phrase} +20 XP +15 Tokens`;
// setMessage(successMsg);

//random praise (this calls speak under the hood)
 const phrase = celebrateSuccess(); // speaks + returns phrase
 //const successMsg = `🎉 ${phrase} +20 XP +15 Tokens`;

 const successMsg = `Trendlines are diagonal lines drawn on a price chart connecting a series of swing lows (for an uptrend) or swing highs (for a downtrend) to show the direction of a trend.`;

 
 setMessage(successMsg);




        // TTS compliment (will work because ensureUserAudioOnce has armed audio)
        //say("Sharp move!");

        confetti(e.currentTarget, overlay);
        if (hostEl && guide.end) {
          burstConfettiAt(hostEl, guide.end, 42);
          bullPopAt(hostEl, guide.end, "🐂");
        }

        pushDL("gesture_success", {
          lesson_id: "flash_arrow_up",
          score: Number(res.score.toFixed(3)),
          duration_ms: durationMs,
        });

        // Notify shell
        //if (typeof onComplete === "function") onComplete({ message: successMsg });
        if (typeof onComplete === "function") onComplete({ message: successMsg });
        if (typeof onContinue === "function") onContinue(); // back-compat
      } else {
        setMessage("Almost! Start at the label and trace the dashed arrow ↗");
        playMissThunk();
        setTimeout(() => setHintOn(true), 1800);
        pushDL("gesture_retry", {
          lesson_id: "flash_arrow_up",
          score: Number(res.score.toFixed(3)),
          duration_ms: durationMs,
        });
      }
    };

    // Paths
    const strokeD = pts.length
      ? `M ${pts[0].x},${pts[0].y} ` + pts.slice(1).map(p => `L ${p.x},${p.y}`).join(" ")
      : null;
    const guideD = `M ${guide.start.x},${guide.start.y} L ${guide.end.x},${guide.end.y}`;

    // CoachMark anchor
    const hintTarget = { x: guide.start.x, y: guide.start.y };
    const hintTail   = { x: guide.start.x, y: guide.start.y };
    const hintBounds = { w: overlay.w, h: overlay.h, pad: 12 };


    // --- ADD/EXPORT: user-gesture arming + TTS ---

let _ctx;
function getCtx() {
  try {
    if (!_ctx) _ctx = new (window.AudioContext || window.webkitAudioContext)();
  } catch {}
  return _ctx;
}




    return (
      <>
        <OverlayCanvas
          overlay={overlay}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          style={{ cursor: "crosshair" }}
        >
          <CoachMarkStyles />

          {/* Dashed guide with arrowhead */}
          <defs>
            <marker id="arrowHead-uptrace" markerWidth="14" markerHeight="14" refX="12" refY="6" orient="auto" markerUnits="strokeWidth">
              <path d="M 0 0 L 12 6 L 0 12 z" fill="#FFB800" />
            </marker>
          </defs>
          <path
            d={guideD}
            fill="none"
            stroke="#FFB800"
            strokeWidth="4"
            strokeDasharray="8 8"
            markerEnd="url(#arrowHead-uptrace)"
          />

          {/* Learner stroke */}
          {strokeD && (
            <path
              d={strokeD}
              fill="none"
              stroke={result ? "#2ECC71" : "#FF7C24"}
              strokeWidth="4"
              strokeLinecap="round"
            />
          )}

          {/* Coach mark */}
          {hintOn && (
            <CoachMark
              target={hintTarget}
              tail={hintTail}
              label="Start here, draw up ↗"
              variant="hand"
              bounds={hintBounds}
              show={hintOn}
            />
          )}
        </OverlayCanvas>

        {/* Feedback only (no mid-card CTA) */}
        {message && (
          <div
            style={{ marginTop: 12, fontWeight: 800, color: result ? "#2ECC71" : "#FFB800", textAlign: "center" }}
            aria-live="polite"
          >
            {message}
          </div>
        )}
      </>
    );
  };

  // Card wrapper + chart render
  return (
    <div style={{ maxWidth: 960, margin: "20px auto", background: "#0F141B", border: "1px solid #1E2630", borderRadius: 16, padding: 16 }}>
      <h2 style={{ color: "#FF7C24", fontSize: 24, margin: "6px 0 12px", textAlign: "center", fontFamily: "Nunito Sans, sans-serif" }}>
        {title}
      </h2>
      <ChartPane height={280} candles={candles}>
        {(ctx) => <OverlayBlock ctx={ctx} />}
      </ChartPane>
    </div>
  );
}
